import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useCreateUserAccessRequest } from '@client-app-sdk';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import routes from '@routes';
import { BundleCombobox } from '../components/BundleCombobox';
import { AccessUnitCombobox } from '../components/AccessUnitCombobox';
import LabeledTextField from '@common/ui/LabeledTextField';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';
import { ComboboxOption } from '@common/ui/Combobox/Combobox';
import { ensureArray } from '@MyRequests/helpers';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import useListAccessBundles from '@client-app-sdk/hooks/inventory/use-list-access-bundles';

interface BundleFormState {
  selectedBundle: ComboboxOption | null;
  selectedAccessUnit: ComboboxOption[] | null;
  justification: string;
}

export function BundleRequestForm() {
  const { isEnabled: isHideBundleContent } = useFlagFixed(Flags.HIDE_BUNDLE_CONTENT);
  const { data, isFetched } = useListAccessBundles({ limit: 1 });
  const hasBundles = !isFetched || !!data?.pages[0]?.data.length;

  const [formState, setFormState] = useState<BundleFormState>({
    selectedBundle: null,
    selectedAccessUnit: [],
    justification: '',
  });

  const { track } = useAnalyticsContext();

  const updateFormState = useCallback(
    (field: keyof BundleFormState, value: ComboboxOption | ComboboxOption[] | string | null) => {
      let newFormState = { ...formState, [field]: value };
      switch (field) {
        case 'selectedBundle':
          newFormState = {
            ...newFormState,
            selectedAccessUnit: [],
          };
          break;
        default:
          break;
      }
      setFormState(newFormState);
    },
    [formState],
  );

  const isFormDataEmpty = !formState.selectedBundle?.value || formState.justification.length === 0;

  const navigate = useNavigate();
  const { createUserAccessRequest, isLoading, isSuccess, isError } = useCreateUserAccessRequest();

  useEffect(() => {
    if (isSuccess) {
      toast.success('Access request created successfully');
      track(AnalyticsEvents.BundleRequestSent);
      navigate(routes.OpenRequests.path);
    }
    if (isError) {
      toast.error('Error creating access request');
      track(AnalyticsEvents.BundleRequestFailed);
    }
  }, [isSuccess, isError, navigate, track]);

  const handleRequest = async () => {
    if (isFormDataEmpty) return;
    const createAccessRequestClientModel = {
      filter_bundle_ids: ensureArray(formState.selectedBundle?.value ?? []),
      filter_resource_ids: [],
      filter_resources: [],
      filter_permission_ids: [],
      filter_integration_ids: [],
      filter_access_unit_ids: ensureArray(formState.selectedAccessUnit?.map((au) => au.value) ?? []),
      filter_resource_type_ids: [],
      justification: formState.justification,
    };
    createUserAccessRequest({ createAccessRequestClientModel });
  };

  const AccessUnitsSelector = useMemo(() => {
    if (isHideBundleContent) return null;

    if (formState.selectedBundle?.value) {
      return (
        <AccessUnitCombobox
          value={formState.selectedAccessUnit}
          bundleIds={[formState.selectedBundle.value]}
          onChange={(value) => updateFormState('selectedAccessUnit', value)}
        />
      );
    }

    return (
      <Stack direction="row" spacing={2} alignItems="end">
        <LabeledTextField disabled label="Resources" placeholder="Select resources" />
        <FormControlLabel
          sx={{ width: '12rem' }}
          control={<Switch disabled />}
          label={<Typography variant="caption">Entire bundle</Typography>}
        />
      </Stack>
    );
  }, [formState, isHideBundleContent, updateFormState]);

  return (
    <Stack spacing={2} width="36rem">
      {!hasBundles && (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Uh oh! You don’t have any bundles to request</AlertTitle>
          Bundles are created by Apono admins. If you believe you need access to a bundle, talk to your manager or
          admin. Then, come back and request access.
        </Alert>
      )}
      <BundleCombobox value={formState.selectedBundle} onChange={(value) => updateFormState('selectedBundle', value)} />
      {AccessUnitsSelector}
      <LabeledTextField
        label="Justification"
        size="small"
        minRows={2}
        multiline
        value={formState.justification}
        onChange={(e) => updateFormState('justification', e.target.value)}
      />
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <LoadingButton
          data-trigger={AnalyticsEvents.BundleRequestSendClicked}
          variant="contained"
          color="primary"
          onClick={handleRequest}
          loading={isLoading}
          disabled={isFormDataEmpty}
        >
          Request
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
